import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Popper,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { FaFilePdf } from 'react-icons/fa';
import { IoLinkSharp, IoApps } from 'react-icons/io5';
import { MdCampaign } from 'react-icons/md';
import { FaBrain, FaChartBar } from 'react-icons/fa';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import SearchIcon from '@mui/icons-material/Search';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { formatTimeAgo as formatTimeAgoUtil } from '../utils/dateUtils';
import { usePermissions } from '../context/PermissionContext';
import { useFavorites } from '../context/FavoritesContext';
import { hasUnseenWhatsNew } from './ViewUpdates';
import API_BASE_URL from '../config/api';

const RECENT_VISITS_KEY = 'recentPageVisits';
const WHATS_NEW_RIBBON_DISMISSED = 'dashboardHome_whatsNewRibbon_dismissedAt';

const DashboardHome = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAdmin, isOwner, hasAccessToPath } = usePermissions();
  const { favorites } = useFavorites();
  const { setChatOpen, setSearchOpen, setShortcutsOpen, setCommandPaletteOpen } = useOutletContext() || {};
  const [greeting, setGreeting] = useState('');
  const [recentActivities, setRecentActivities] = useState([]);
  const [ribbonDismissed, setRibbonDismissed] = useState(false);
  // Interface keys assigned to the *default* role — what every fresh user
  // is guaranteed to be able to open. Loaded once from the backend and
  // used to constrain "Tool of the Day" so the spotlight never points at
  // a restricted/admin-only interface that a regular user couldn't access.
  const [defaultRoleInterfaces, setDefaultRoleInterfaces] = useState(null);
  const [hoverCategoryAnchor, setHoverCategoryAnchor] = useState(null);
  const [hoverCategoryData, setHoverCategoryData] = useState(null);
  const peekCloseTimerRef = React.useRef(null);
  // Widget full-screen — when set to a widget id, only that widget renders
  // expanded and others are hidden. Persisted in localStorage so it sticks
  // across refreshes / page navigations.
  const [maximizedWidget, setMaximizedWidget] = useState(() => {
    try {
      const raw = localStorage.getItem('dashboardHome_maximizedWidget');
      if (raw === 'recent' || raw === 'quick') return raw;
    } catch { /* ignore */ }
    return null;
  });
  useEffect(() => {
    try {
      if (maximizedWidget) localStorage.setItem('dashboardHome_maximizedWidget', maximizedWidget);
      else localStorage.removeItem('dashboardHome_maximizedWidget');
    } catch { /* ignore */ }
  }, [maximizedWidget]);
  // Drag-reorder of bottom widgets — persisted in localStorage. The two
  // baseline widgets are 'recent' (Recent Activity) and 'quick' (Quick
  // Actions); user can swap order. Default order is the original layout.
  const [widgetOrder, setWidgetOrder] = useState(() => {
    try {
      const raw = localStorage.getItem('dashboardHome_widgetOrder');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length === 2) return parsed;
      }
    } catch { /* ignore */ }
    return ['recent', 'quick'];
  });
  const [draggedWidget, setDraggedWidget] = useState(null);

  // Customizable Quick Actions — paths the user has pinned. Announcements
  // is always at index 0 (locked). Up to 8 total. Persisted in localStorage.
  const ANNOUNCEMENTS_PATH = '/dashboard/view-updates';
  const QUICK_ACTIONS_DEFAULT = [
    ANNOUNCEMENTS_PATH,
    '/dashboard/aws-file-links',
    '/dashboard/po-distribution',
    '/dashboard/ai-image-generator',
    '/dashboard/excel-reports',
    '/dashboard/customer-style-search',
  ];
  const QUICK_ACTIONS_MAX = 8;
  const [quickActionsPaths, setQuickActionsPaths] = useState(() => {
    try {
      const raw = localStorage.getItem('dashboardHome_quickActions_v1');
      if (raw) {
        let parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.every(p => typeof p === 'string')) {
          // Dedupe
          parsed = [...new Set(parsed)];
          // Ensure Announcements is *present* somewhere — but preserve
          // its position if the user reordered it. If missing, prepend.
          if (!parsed.includes(ANNOUNCEMENTS_PATH)) {
            parsed.unshift(ANNOUNCEMENTS_PATH);
          }
          // Cap at 8
          if (parsed.length > QUICK_ACTIONS_MAX) parsed = parsed.slice(0, QUICK_ACTIONS_MAX);
          return parsed;
        }
      }
    } catch { /* ignore */ }
    return QUICK_ACTIONS_DEFAULT;
  });
  const [quickActionsEditMode, setQuickActionsEditMode] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [draggedQuickIdx, setDraggedQuickIdx] = useState(null);
  // Persist whenever the list changes.
  useEffect(() => {
    try { localStorage.setItem('dashboardHome_quickActions_v1', JSON.stringify(quickActionsPaths)); } catch { /* ignore */ }
  }, [quickActionsPaths]);
  const isDark = theme.palette.mode === 'dark';

  // ─── Greeting + initial loads ──────────────────────────────────────────
  useEffect(() => {
    const hour = new Date().getHours();
    const userEmail = localStorage.getItem('userEmail') || 'User';
    const firstName = userEmail.split('@')[0].split('.')[0];
    const capitalizedName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    if (hour < 12) setGreeting(`Good morning, ${capitalizedName}`);
    else if (hour < 17) setGreeting(`Good afternoon, ${capitalizedName}`);
    else setGreeting(`Good evening, ${capitalizedName}`);

    try {
      const stored = localStorage.getItem(RECENT_VISITS_KEY);
      const visits = stored ? JSON.parse(stored) : [];
      const landingPages = ['Dashboard Home', 'PDF Tools', 'Excel Tools', 'Link Management', 'Other Tools', 'AI Tools', "What's New"];
      const interfaceVisits = visits.filter(v => !landingPages.includes(v.text));
      setRecentActivities(interfaceVisits.slice(0, 6).map((v, i) => ({
        id: i + 1, action: v.text, time: new Date(v.timestamp), type: v.type, path: v.path,
      })));
    } catch {
      setRecentActivities([]);
    }

    try {
      const dismissedAt = localStorage.getItem(WHATS_NEW_RIBBON_DISMISSED);
      if (dismissedAt) {
        const today = new Date().toISOString().slice(0, 10);
        if (dismissedAt.slice(0, 10) === today) setRibbonDismissed(true);
      }
    } catch { /* ignore */ }

    // Fetch the default role's interfaces so Tool of the Day can pick a
    // tool any user can open. Best-effort: on failure we just leave
    // defaultRoleInterfaces as [] which means we won't show the spotlight.
    (async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        const res = await fetch(`${API_BASE_URL}/api/AccessControl/default-role-interfaces`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data?.success && Array.isArray(data.interfaces)) {
          setDefaultRoleInterfaces(data.interfaces);
        } else {
          setDefaultRoleInterfaces([]);
        }
      } catch {
        setDefaultRoleInterfaces([]);
      }
    })();
  }, []);

  // ─── Permission filter / category data ─────────────────────────────────
  const categoryInterfaces = {
    '/dashboard/pdf-tools': [
      'ama9999-ucc-labels', 'amazon-di-ucc-label', 'bilingual-labels', 'carton-labels',
      'expense-allocation-invoices', 'home-goods-pdf', 'pdf-manipulation', 'pdf-reports',
      'pdf-to-excel-ucc-labels', 'pallet-labels', 'pdq-full-pallet-pull-label', 'cad-invoices',
      'master-retail-rugs', 'label-printing',
    ],
    '/dashboard/excel-tools': [
      'excel-export-item-images', 'items-detail', 'po-distribution', 'po-distribution-whs', 'sales-orders-export', 'excel-reports',
      'canadian-13-provinces-freight', 'eu-freight',
    ],
    '/dashboard/link-tools': ['aws-file-links', 'aws-file-links-alternate', 'wayfair-asset-upload', 'amazon-bulk-image-uploader', 'broken-link-checker', 'asset-explorer', 'bulk-image-downloader'],
    '/dashboard/other-tools': [
      '48-state-freight', 'customer-style-search', 'document-translation',
      'marketing-copies', 'product-details', 'remove-background', 'price-scraper',
      'social-media-content', 'product-data-validator', 'email-campaign-builder', 'barcode-generator', 'file-converter', 'pallet-optimizer', 'reports-scheduler',
    ],
    '/dashboard/ai-tools': ['ai-image-generator', 'ai-video-generator', 'seo-content-writer', 'ai-image-search', 'rug-room-shots'],
    '/dashboard/analytics-tools': ['sales-dashboard', 'sales-dashboard-v2', 'returns-dashboard', 'new-items', 'customer-style-gaps', 'collection-launches', 'sales-pulse', 'retail-os', 'reorder-console'],
  };
  const getCount = (path) => {
    const interfaces = categoryInterfaces[path] || [];
    if (isAdmin || isOwner) return interfaces.length;
    return interfaces.filter(iface => hasAccessToPath(iface)).length;
  };
  const allToolCategories = [
    { title: 'PDF Tools', icon: FaFilePdf, color: '#DC143C', description: 'Generate labels, manipulate PDFs, create invoices and manage document workflows', path: '/dashboard/pdf-tools', peekVariant: 'minimal' },
    { title: 'Excel Tools', icon: RiFileExcel2Fill, color: '#228B22', description: 'Export reports, analyze inventory, manage product data and process bulk operations', path: '/dashboard/excel-tools', peekVariant: 'minimal' },
    { title: 'Link Management', icon: IoLinkSharp, color: '#4169E1', description: 'Manage AWS links, image URLs, video content and file distribution', path: '/dashboard/link-tools', peekVariant: 'minimal' },
    { title: 'Other Tools', icon: IoApps, color: '#FF8C00', description: 'Specialized tools for freight, translations, testing and various business operations', path: '/dashboard/other-tools', peekVariant: 'minimal' },
    { title: 'AI Tools', icon: FaBrain, color: '#722F37', description: 'AI-powered tools for product image generation, content creation and automation', path: '/dashboard/ai-tools', peekVariant: 'minimal' },
    { title: 'Analytics', icon: FaChartBar, color: '#2FC4B2', description: 'Live dashboards and business intelligence: sales, returns, revenue and more', path: '/dashboard/analytics-tools', peekVariant: 'minimal' },
  ];
  const toolCategories = (isAdmin || isOwner) ? allToolCategories : allToolCategories.filter(cat => getCount(cat.path) > 0);

  // ─── Tool of the Day ───────────────────────────────────────────────────
  // Random pick from interfaces the *default* role has access to. The
  // pick is cached in localStorage by date so it stays the same on every
  // refresh / navigation throughout the day — only re-rolls when the
  // calendar day flips (or if the cached tool drops out of the eligible
  // list, e.g. permissions change).
  const toolOfTheDay = useMemo(() => {
    if (!defaultRoleInterfaces || defaultRoleInterfaces.length === 0) return null;
    const knownSlugs = new Set();
    for (const [, slugs] of Object.entries(categoryInterfaces)) {
      for (const slug of slugs) knownSlugs.add(slug);
    }
    const eligible = defaultRoleInterfaces.filter(slug => knownSlugs.has(slug));
    if (!eligible.length) return null;

    const TOOL_OF_THE_DAY_KEY = 'toolOfTheDay_v1';
    const today = new Date().toISOString().slice(0, 10);

    let chosen = null;
    try {
      const raw = localStorage.getItem(TOOL_OF_THE_DAY_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.date === today && eligible.includes(cached.slug)) {
          chosen = cached.slug;
        }
      }
    } catch { /* ignore parse errors, fall through to fresh pick */ }

    if (!chosen) {
      chosen = eligible[Math.floor(Math.random() * eligible.length)];
      try {
        localStorage.setItem(TOOL_OF_THE_DAY_KEY, JSON.stringify({ date: today, slug: chosen }));
      } catch { /* ignore quota errors */ }
    }

    return {
      slug: chosen,
      title: chosen.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      path: `/dashboard/${chosen}`,
    };
    // eslint-disable-next-line
  }, [defaultRoleInterfaces]);

  const showWhatsNewRibbon = !ribbonDismissed && hasUnseenWhatsNew();
  const dismissRibbon = () => {
    setRibbonDismissed(true);
    try { localStorage.setItem(WHATS_NEW_RIBBON_DISMISSED, new Date().toISOString()); } catch { /* ignore */ }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'excel': return <RiFileExcel2Fill size={20} color="#228B22" />;
      case 'pdf': return <FaFilePdf size={20} color="#DC143C" />;
      case 'link': return <IoLinkSharp size={20} color="#4169E1" />;
      case 'admin': return <AdminPanelSettingsIcon sx={{ fontSize: 20, color: '#7C3AED' }} />;
      case 'ai': return <FaBrain size={20} color="#722F37" />;
      case 'reports': return <FaChartBar size={20} color="#00897B" />;
      case 'other': return <IoApps size={20} color="#FF8C00" />;
      default: return <IoApps size={20} />;
    }
  };
  const formatTimeAgo = (date) => formatTimeAgoUtil(date);

  // ─── Tool peek (deck-style hover dropdown) ─────────────────────────────
  const cancelPeekClose = () => {
    if (peekCloseTimerRef.current) {
      clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
  };
  const handleCategoryHover = (e, cat) => {
    cancelPeekClose();
    setHoverCategoryAnchor(e.currentTarget);
    setHoverCategoryData(cat);
  };
  const scheduleCategoryClose = () => {
    cancelPeekClose();
    peekCloseTimerRef.current = setTimeout(() => {
      setHoverCategoryAnchor(null);
      setHoverCategoryData(null);
    }, 350);
  };
  const niceName = (slug) => slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Inline-SVG generator for the wavy "underline-on-hover" decoration the
  // category landings (e.g. PDFLanding) use under each tool card. Reused
  // here so the Available Tools cards and Recent Activity rows get the
  // same animated underline. URL-encodes the leading "#" of the hex color
  // (data: URLs can't carry a raw #).
  const wavyUnderlineSvg = (hexColor) => {
    const enc = (hexColor || '#000').replace('#', '%23');
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 10' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q25 0 50 5 T100 5' fill='none' stroke='${enc}' stroke-width='4'/%3E%3C/svg%3E")`;
  };

  // All admin tools share the violet identity color so they read as a
  // single family in the dashboard's Recent Activity and AdminLanding.
  const ADMIN_VIOLET = '#7C3AED';
  const ADMIN_TOOL_COLORS = {
    'admin/users':         ADMIN_VIOLET,
    'admin/roles':         ADMIN_VIOLET,
    'admin/file-history':  ADMIN_VIOLET,
    'admin/notifications': ADMIN_VIOLET,
    'admin-tools':         ADMIN_VIOLET,
  };

  // Map an interface path (e.g. /dashboard/pdf-manipulation) to the color
  // of the category that owns it. Used so each Recent Activity row's
  // underline matches the tool's own category color. Falls back to the
  // theme primary for paths outside the standard categories.
  const getCategoryColorForPath = (path) => {
    const slug = (path || '').replace('/dashboard/', '');
    if (ADMIN_TOOL_COLORS[slug]) return ADMIN_TOOL_COLORS[slug];
    for (const [catPath, slugs] of Object.entries(categoryInterfaces)) {
      if (slugs.includes(slug)) {
        const cat = allToolCategories.find((c) => c.path === catPath);
        if (cat) return cat.color;
      }
    }
    return theme.palette.primary.main;
  };

  // Admin-only routes — usable in Quick Actions when the user has admin/
  // owner role. These don't live in `categoryInterfaces` because they're
  // outside the regular tool categories, so we list them explicitly.
  const ADMIN_QUICK_ACTIONS = [
    { path: '/dashboard/admin/users',         label: 'User Management',     icon: AdminPanelSettingsIcon },
    { path: '/dashboard/admin/roles',         label: 'Role Management',     icon: AdminPanelSettingsIcon },
    { path: '/dashboard/admin/file-history',  label: 'File History',         icon: AdminPanelSettingsIcon },
    { path: '/dashboard/admin/notifications', label: 'Send Notifications',  icon: AdminPanelSettingsIcon },
  ];

  // Map a Quick Action path → display info (label, icon, type) using the
  // category that owns that interface so colors match elsewhere.
  const getQuickActionInfo = (path) => {
    if (path === ANNOUNCEMENTS_PATH) {
      return { label: 'Announcements', icon: MdCampaign, type: 'other', locked: true };
    }
    // Admin shortcuts
    const adminEntry = ADMIN_QUICK_ACTIONS.find(a => a.path === path);
    if (adminEntry) {
      return { label: adminEntry.label, icon: adminEntry.icon, type: 'admin', locked: false };
    }
    const slug = path.replace(/^\/dashboard\//, '');
    const label = niceName(slug);
    let icon = IoApps, type = 'other';
    for (const [catPath, slugs] of Object.entries(categoryInterfaces)) {
      if (slugs.includes(slug)) {
        if (catPath === '/dashboard/pdf-tools')      { icon = FaFilePdf;     type = 'pdf'; }
        else if (catPath === '/dashboard/excel-tools')   { icon = RiFileExcel2Fill; type = 'excel'; }
        else if (catPath === '/dashboard/link-tools')    { icon = IoLinkSharp;      type = 'link'; }
        else if (catPath === '/dashboard/ai-tools')      { icon = FaBrain;          type = 'ai'; }
        else if (catPath === '/dashboard/analytics-tools') { icon = FaChartBar;       type = 'reports'; }
        else                                             { icon = IoApps;           type = 'other'; }
        break;
      }
    }
    return { label, icon, type, locked: false };
  };

  const goTo = (slug) => () => {
    navigate(`/dashboard/${slug}`);
    cancelPeekClose();
    setHoverCategoryAnchor(null);
    setHoverCategoryData(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* What's New ribbon — auto-hides if no unseen entries; user can
          dismiss for the day with the X button. */}
      {showWhatsNewRibbon && (
        <Paper
          elevation={0}
          sx={{
            mb: 3, p: 1.5, borderRadius: 2,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}08 100%)`,
            border: `1px solid ${theme.palette.primary.main}40`,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}
        >
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Something new just shipped — check What's New
            </Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            endIcon={<ChevronRightIcon />}
            onClick={() => navigate('/dashboard/view-updates')}
            sx={{ textTransform: 'none' }}
          >
            View
          </Button>
          <IconButton size="small" onClick={dismissRibbon} aria-label="Dismiss">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      {/* Greeting + small keyboard-shortcut chips */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {greeting}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          Welcome to Theezdocs Dashboard - Your central hub for all data processing tools
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Open the global search">
            <Chip
              size="small"
              variant="outlined"
              icon={<KeyboardIcon sx={{ fontSize: 14 }} />}
              label="Press / to search"
              onClick={() => setSearchOpen && setSearchOpen(true)}
              sx={{ height: 22, fontSize: 11, cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Open command palette">
            <Chip
              size="small"
              variant="outlined"
              icon={<KeyboardIcon sx={{ fontSize: 14 }} />}
              label="⌘K · Command palette"
              onClick={() => setCommandPaletteOpen && setCommandPaletteOpen(true)}
              sx={{ height: 22, fontSize: 11, cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Show all keyboard shortcuts">
            <Chip
              size="small"
              variant="outlined"
              icon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
              label="Press ? for shortcuts"
              onClick={() => setShortcutsOpen && setShortcutsOpen(true)}
              sx={{ height: 22, fontSize: 11, cursor: 'pointer' }}
            />
          </Tooltip>
        </Stack>
      </Box>

      {/* Workspace launchpad — whole card clickable. Tinted surface so it
          stands out (esp. light mode); lifts on hover like the Favorites /
          Available Tools cards; a band of light also travels around the
          border (rotating conic-gradient under a 2px ring). */}
      <Box
        onClick={() => navigate('/dashboard/workspace')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/dashboard/workspace'); } }}
        sx={{
          position: 'relative', mb: 3, borderRadius: 2, p: '2px', overflow: 'hidden',
          cursor: 'pointer',
          // Neutral border at rest; the primary highlight + moving glow only
          // appear on hover.
          bgcolor: 'divider',
          transition: 'transform 200ms ease, box-shadow 200ms ease',
          '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[6] },
          '&:hover .ws-glow': { opacity: 1 },
          '&:hover .ws-chevron': { transform: 'translateX(4px)' },
          '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        }}
      >
        <Box
          className="ws-glow"
          sx={{
            position: 'absolute', inset: '-60%', zIndex: 0, opacity: 0,
            transition: 'opacity 300ms ease',
            background: `conic-gradient(from 0deg, transparent 0deg, ${theme.palette.primary.main} 55deg, ${theme.palette.primary.light} 85deg, transparent 135deg, transparent 360deg)`,
            animation: 'wsBorderSpin 3s linear infinite',
            '@keyframes wsBorderSpin': { to: { transform: 'rotate(360deg)' } },
          }}
        />
        <Box
          sx={{
            position: 'relative', zIndex: 1, borderRadius: 1.7,
            p: 2, display: 'flex', alignItems: 'center', gap: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{
            width: 50, height: 50, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'primary.main', color: 'primary.contrastText',
            boxShadow: `0 4px 12px ${theme.palette.primary.main}55`,
          }}>
            <SpaceDashboardRoundedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Your Workspace</Typography>
            <Typography variant="body2" color="text.secondary">Profile, tasks, and a password generator — more tools coming soon.</Typography>
          </Box>
          <Button
            variant="contained"
            onClick={(e) => { e.stopPropagation(); navigate('/dashboard/workspace'); }}
            endIcon={<ChevronRightIcon className="ws-chevron" sx={{ transition: 'transform 200ms ease' }} />}
            sx={{ flexShrink: 0 }}
          >
            Open Workspace
          </Button>
        </Box>
      </Box>

      {/* Your Favorites */}
      {(favorites && favorites.length > 0) ? (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <StarIcon sx={{ color: '#FFC107', fontSize: 22 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Your Favorites</Typography>
            <Chip size="small" label={favorites.length} sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
          </Stack>
          <Box sx={{
            display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1,
            // Theme-aware scrollbar so the dark mode doesn't render the
            // default white Webkit gutter against the dark dashboard.
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: isDark ? '#555' : '#bbb',
              borderRadius: 4,
              '&:hover': { bgcolor: isDark ? '#777' : '#999' },
            },
            scrollbarColor: isDark ? '#555 transparent' : '#bbb transparent',
            scrollbarWidth: 'thin',
          }}>
            {favorites.map((f) => (
              <Card
                key={f.path}
                onClick={() => navigate(f.path)}
                sx={{
                  minWidth: 180, cursor: 'pointer', flexShrink: 0,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 200ms',
                  '&:hover': {
                    borderColor: 'primary.main', transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3],
                  },
                }}
              >
                <CardContent sx={{ py: 1.5, px: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <StarIcon sx={{ color: '#FFC107', fontSize: 16 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.text}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            mb: 3, p: 1.75, borderRadius: 2, border: `1px dashed ${theme.palette.divider}`,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}
        >
          <StarBorderIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Star tools to pin them here for one-click access. Click the ☆ icon next to any tool's name.
          </Typography>
        </Paper>
      )}

      {/* Tool Categories — 3 per row, hover-peek deck variant */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Available Tools
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {toolCategories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.title}>
            <Card
              onMouseEnter={(e) => handleCategoryHover(e, category)}
              onMouseLeave={scheduleCategoryClose}
              sx={{
                height: '100%', position: 'relative', overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                  borderColor: category.color,
                },
                // Wavy underline that grows from center on hover, mirrors
                // the category-landing tool cards (e.g. PDFLanding).
                '&::after': {
                  content: '""', position: 'absolute',
                  bottom: 0, left: '50%',
                  width: '0%', height: '4px',
                  background: category.color,
                  borderRadius: '2px',
                  transform: 'translateX(-50%)',
                  transition: 'width 0.4s ease-in-out',
                  backgroundImage: wavyUnderlineSvg(category.color),
                  backgroundSize: '100% 100%',
                  pointerEvents: 'none',
                },
                '&:hover::after': { width: '90%' },
              }}
            >
              <CardActionArea onClick={() => navigate(category.path)} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar sx={{
                    width: 80, height: 80,
                    bgcolor: `${category.color}15`, color: category.color,
                    mx: 'auto', mb: 2,
                  }}>
                    <category.icon size={40} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {category.title}
                  </Typography>
                  <Chip
                    label={`${getCount(category.path)} tool${getCount(category.path) !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      mb: 2, bgcolor: `${category.color}20`,
                      color: theme.palette.text.primary, fontWeight: 600,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tool peek — six per-category visual variants for review on
          localhost. Production should pick a single variant; this dispatcher
          is here so the user can compare. */}
      <Popper
        open={Boolean(hoverCategoryAnchor)}
        anchorEl={hoverCategoryAnchor}
        // For the `bubbles` variant we want the popup to *overlay* the
        // category card instead of dropping below it — bubbles burst out
        // from the card center. We achieve that by anchoring the popper
        // to the bottom of the card and pushing it back up with a large
        // negative Y offset so its center aligns with the card center.
        placement={hoverCategoryData?.peekVariant === 'bubbles' ? 'bottom' : 'bottom'}
        transition
        sx={{ zIndex: 1200 }}
        modifiers={[
          { name: 'offset', options: { offset: hoverCategoryData?.peekVariant === 'bubbles' ? [0, -260] : [0, 8] } },
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 12, enabled: hoverCategoryData?.peekVariant !== 'bubbles' } },
          { name: 'flip', options: { fallbackPlacements: ['top', 'right', 'left'], enabled: hoverCategoryData?.peekVariant !== 'bubbles' } },
        ]}
      >
        {({ TransitionProps }) => {
          if (!hoverCategoryData) return <Fade {...TransitionProps}><div /></Fade>;
          const variant = hoverCategoryData.peekVariant || 'deck';
          const accessibleSlugs = (categoryInterfaces[hoverCategoryData.path] || [])
            .filter(slug => isAdmin || isOwner || hasAccessToPath(slug));
          const customScrollbar = {
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: isDark ? '#555' : '#ccc', borderRadius: 4,
              '&:hover': { bgcolor: isDark ? '#777' : '#999' },
            },
          };
          const openCategoryLanding = () => {
            navigate(hoverCategoryData.path);
            scheduleCategoryClose();
          };
          const headerCommon = (
            <Box sx={{
              px: 2, py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: 'flex', alignItems: 'center', gap: 1.25,
              bgcolor: `${hoverCategoryData.color}14`,
            }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: `${hoverCategoryData.color}25`, color: hoverCategoryData.color }}>
                <hoverCategoryData.icon size={18} />
              </Avatar>
              {/* Title block is its own click target — keyboard-focusable
                  via role="button" + tabIndex, opens the category landing. */}
              <Box
                onClick={openCategoryLanding}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCategoryLanding(); } }}
                role="button"
                tabIndex={0}
                aria-label={`Open ${hoverCategoryData.title} landing`}
                sx={{
                  flex: 1, minWidth: 0,
                  cursor: 'pointer',
                  borderRadius: 0.75,
                  px: 0.5, mx: -0.5,
                  transition: 'background-color 160ms ease',
                  '& .peek-header-title': {
                    position: 'relative', display: 'inline-block',
                    transition: 'color 200ms',
                  },
                  '& .peek-header-title::after': {
                    content: '""', position: 'absolute',
                    left: 0, bottom: -2, height: 2, width: 0,
                    bgcolor: hoverCategoryData.color,
                    transition: 'width 240ms cubic-bezier(0.16,1,0.3,1)',
                  },
                  '&:hover .peek-header-title, &:focus-visible .peek-header-title': {
                    color: hoverCategoryData.color,
                  },
                  '&:hover .peek-header-title::after, &:focus-visible .peek-header-title::after': {
                    width: '100%',
                  },
                  '&:focus-visible': { outline: 'none' },
                }}
              >
                <Typography component="span" variant="subtitle2" className="peek-header-title" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  {hoverCategoryData.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {accessibleSlugs.length} tool{accessibleSlugs.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Tooltip title={`Open ${hoverCategoryData.title} landing`}>
                <IconButton size="small" onClick={openCategoryLanding}
                  sx={{ color: hoverCategoryData.color }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
          const emptyState = (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No tools available in this category.
            </Typography>
          );

          // ── BUBBLES — tools burst from the card as floating pills ──
          //   Instead of dropping a panel below the card, the popup
          //   overlays the card area and bubbles fly outward from its
          //   center to a scattered cluster. Each bubble is a clickable
          //   pill labeled with the tool name; hover scales it up with
          //   a bouncy spring and a colored halo. The bubbles bob gently
          //   even at rest. Click any bubble to navigate.
          if (variant === 'bubbles') {
            // Pseudo-random but stable position per bubble — keeps the
            // cluster feeling organic without re-arranging on re-render.
            const seedRand = (seed) => {
              const x = Math.sin(seed * 9301 + 49297) * 10000;
              return x - Math.floor(x);
            };
            // Place bubbles around the card center on staggered radii
            // and angles, so they look like they exploded outward.
            const positions = accessibleSlugs.map((_, i) => {
              const ringIdx = Math.floor(i / 4);                    // 0, 1, 2, ...
              const ringSize = 4 + ringIdx;                         // 4 → 5 → 6 → ...
              const angleStep = (Math.PI * 2) / ringSize;
              const baseAngle = (i % ringSize) * angleStep;
              const angleJitter = (seedRand(i + 17) - 0.5) * 0.4;   // ±~12°
              const angle = baseAngle + angleJitter - Math.PI / 2;  // start at top
              const radiusBase = 70 + ringIdx * 70;
              const radiusJitter = (seedRand(i + 31) - 0.5) * 18;
              const radius = radiusBase + radiusJitter;
              return {
                x: Math.cos(angle) * radius,
                y: Math.sin(angle) * radius,
                bobDelay: seedRand(i + 53) * 4, // 0-4s
                bobDur:   3 + seedRand(i + 71) * 2, // 3-5s
              };
            });
            return (
              <Fade {...TransitionProps} timeout={{ enter: 280, exit: 200 }}>
                <Box
                  onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    position: 'relative',
                    width: 460, height: 460,
                    pointerEvents: 'none',  // only the bubbles themselves are clickable
                  }}>
                  {accessibleSlugs.length === 0 ? (
                    <Box sx={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'auto',
                      bgcolor: 'background.paper',
                      px: 2, py: 1, borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        No tools available.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Soft halo behind the bubbles, hinting at the
                          source of the burst. Doubles as a "stay open"
                          hover zone so cursor passing through the center
                          between bubbles doesn't trigger a close. */}
                      <Box
                        onMouseEnter={cancelPeekClose}
                        onMouseLeave={scheduleCategoryClose}
                        sx={{
                          position: 'absolute', top: '50%', left: '50%',
                          width: 160, height: 160,
                          transform: 'translate(-50%, -50%)',
                          borderRadius: '50%',
                          pointerEvents: 'auto',
                          background: `radial-gradient(circle, ${hoverCategoryData.color}30 0%, transparent 70%)`,
                          animation: 'peekBubbleHalo 3.2s ease-in-out infinite',
                          '@keyframes peekBubbleHalo': {
                            '0%, 100%': { opacity: 0.6, transform: 'translate(-50%,-50%) scale(1)' },
                            '50%':      { opacity: 1, transform: 'translate(-50%,-50%) scale(1.15)' },
                          },
                        }}
                      />

                      {/* Faint connector lines from the halo center out
                          to each bubble — makes the burst feel tethered.
                          Each line is an absolutely-positioned thin
                          rectangle rotated to point at its bubble. */}
                      {accessibleSlugs.map((_, i) => {
                        const p = positions[i];
                        const len = Math.sqrt(p.x * p.x + p.y * p.y);
                        const angleDeg = Math.atan2(p.y, p.x) * 180 / Math.PI;
                        return (
                          <Box key={`line-${i}`} sx={{
                            position: 'absolute',
                            top: '50%', left: '50%',
                            width: len, height: 1,
                            transformOrigin: '0% 50%',
                            transform: `rotate(${angleDeg}deg)`,
                            background: `linear-gradient(90deg, ${hoverCategoryData.color}55 0%, ${hoverCategoryData.color}10 100%)`,
                            opacity: 0,
                            animation: 'peekBubbleLineIn 480ms ease-out forwards',
                            animationDelay: `${i * 35 + 80}ms`,
                            pointerEvents: 'none',
                            '@keyframes peekBubbleLineIn': {
                              '0%':   { opacity: 0 },
                              '100%': { opacity: 1 },
                            },
                          }} />
                        );
                      })}

                      {accessibleSlugs.map((slug, i) => {
                        const p = positions[i];
                        // Tiny horizontal phase offset for the sway so
                        // bubbles don't all sway in lockstep.
                        const swayPhase = (i % 3) - 1; // -1, 0, +1
                        return (
                          <Box key={slug}
                            onClick={goTo(slug)}
                            onMouseEnter={cancelPeekClose}
                            onMouseLeave={scheduleCategoryClose}
                            sx={{
                              position: 'absolute',
                              top: '50%', left: '50%',
                              pointerEvents: 'auto',
                              cursor: 'pointer',
                              // Outer transform handles the fly-out from
                              // center; an inner wrapper handles the bob.
                              // Avoid CSS variables in animations — they
                              // sometimes don't tween correctly across
                              // browsers. Use direct values via inline
                              // style + a bespoke @keyframes per index.
                              [`@keyframes peekBubbleFly_${i}`]: {
                                '0%':   { transform: 'translate(-50%, -50%) scale(0)' },
                                '60%':  { transform: `translate(calc(-50% + ${p.x}px), calc(-50% + ${p.y}px)) scale(1.1)` },
                                '100%': { transform: `translate(calc(-50% + ${p.x}px), calc(-50% + ${p.y}px)) scale(1)` },
                              },
                              transform: 'translate(-50%, -50%) scale(0)',
                              animation: `peekBubbleFly_${i} 540ms cubic-bezier(0.34,1.56,0.64,1) forwards`,
                              animationDelay: `${i * 35}ms`,
                              '&:active .bubble-pill': {
                                transform: 'scale(0.92)',
                              },
                            }}>
                            <Box sx={{
                              animation: `peekBubbleBob_${i} ${p.bobDur}s ease-in-out ${p.bobDelay}s infinite`,
                              [`@keyframes peekBubbleBob_${i}`]: {
                                '0%, 100%': { transform: `translate(0px, 0px)` },
                                '50%':      { transform: `translate(${swayPhase * 2}px, -4px)` },
                              },
                            }}>
                              <Box className="bubble-pill" sx={{
                                px: 1.6, py: 0.9, borderRadius: 999,
                                background: `linear-gradient(180deg, ${hoverCategoryData.color} 0%, ${hoverCategoryData.color}DD 100%)`,
                                color: theme.palette.getContrastText(hoverCategoryData.color),
                                fontWeight: 700, whiteSpace: 'nowrap',
                                boxShadow: `
                                  0 6px 16px ${hoverCategoryData.color}60,
                                  inset 0 1px 0 rgba(255,255,255,0.3),
                                  inset 0 -2px 4px rgba(0,0,0,0.18)
                                `,
                                transition: 'transform 220ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 220ms',
                                '&:hover': {
                                  transform: 'scale(1.15)',
                                  boxShadow: `
                                    0 12px 28px ${hoverCategoryData.color},
                                    0 0 0 4px ${hoverCategoryData.color}40,
                                    inset 0 1px 0 rgba(255,255,255,0.35),
                                    inset 0 -2px 6px rgba(0,0,0,0.2)
                                  `,
                                },
                              }}>
                                <Typography variant="caption" sx={{
                                  fontWeight: 700, fontSize: 11.5, letterSpacing: 0.2,
                                }}>
                                  {niceName(slug)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </>
                  )}
                </Box>
              </Fade>
            );
          }

          // ── DECK — refined panel design used as the production peek
          //   popup. Subtle frosted surface with a 1px hairline border in
          //   the theme divider color (not the category color), a thin
          //   accent rail on the top edge in the category color, and a
          //   custom layered shadow. Header is a compact strip (small
          //   tinted square holding the icon, title in semibold, count
          //   chip, landing chevron). Each tool row is a 36px-tall
          //   pill that grows a left accent rail on hover and slides
          //   its trailing chevron into view. Items stagger in left-
          //   to-right with a snappy spring.
          if (variant === 'deck') {
            const accent = hoverCategoryData.color;
            const CategoryIcon = hoverCategoryData.icon;
            return (
              <Fade {...TransitionProps} timeout={{ enter: 200, exit: 140 }}>
                <Paper
                  elevation={0}
                  onMouseEnter={cancelPeekClose}
                  onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 340,
                    maxHeight: 420,
                    borderRadius: 2.5,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${
                      isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'
                    }`,
                    bgcolor: isDark ? 'rgba(28,30,33,0.96)' : 'rgba(255,255,255,0.98)',
                    backdropFilter: 'blur(16px) saturate(140%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(140%)',
                    boxShadow: isDark
                      ? '0 14px 36px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35)'
                      : '0 14px 36px rgba(15,23,42,0.14), 0 2px 8px rgba(15,23,42,0.06)',
                    // Top accent rail — thin colored band that signals the
                    // category without flooding the chrome with its color.
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${accent} 0%, ${accent}80 100%)`,
                    },
                  }}
                >
                  {/* Header strip */}
                  <Box
                    sx={{
                      pt: 1.75,
                      px: 1.75,
                      pb: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      borderBottom: `1px solid ${
                        isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.25,
                        bgcolor: `${accent}18`,
                        color: accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <CategoryIcon size={16} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          lineHeight: 1.2,
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {hoverCategoryData.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          letterSpacing: 0.2,
                        }}
                      >
                        {accessibleSlugs.length} tool{accessibleSlugs.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Tooltip title={`Open ${hoverCategoryData.title}`} arrow>
                      <IconButton
                        size="small"
                        onClick={() => { navigate(hoverCategoryData.path); scheduleCategoryClose(); }}
                        sx={{
                          color: accent,
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          '&:hover': { bgcolor: `${accent}18` },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Tool list */}
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      py: 0.75,
                      px: 0.75,
                      ...customScrollbar,
                    }}
                  >
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={0.25}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box
                            key={slug}
                            onClick={goTo(slug)}
                            sx={{
                              position: 'relative',
                              borderRadius: 1.5,
                              px: 1.5,
                              py: 0.9,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              opacity: 0,
                              transform: 'translateX(-4px)',
                              animation: 'peekDeckIn 260ms cubic-bezier(0.22,1,0.36,1) forwards',
                              animationDelay: `${Math.min(i, 14) * 22}ms`,
                              transition: 'background-color 160ms ease, padding-left 160ms ease',
                              // Left accent rail — collapsed by default,
                              // slides in vertically on hover.
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 4,
                                top: '22%',
                                bottom: '22%',
                                width: 3,
                                borderRadius: 2,
                                bgcolor: accent,
                                opacity: 0,
                                transform: 'scaleY(0.4)',
                                transformOrigin: 'center',
                                transition: 'opacity 160ms ease, transform 200ms cubic-bezier(0.22,1,0.36,1)',
                              },
                              '&:hover': {
                                bgcolor: isDark ? `${accent}1F` : `${accent}10`,
                                pl: 2,
                                '&::before': { opacity: 1, transform: 'scaleY(1)' },
                                '& .peek-arrow': { opacity: 1, transform: 'translateX(0)' },
                                '& .peek-name': { color: 'text.primary' },
                              },
                              '@keyframes peekDeckIn': {
                                '0%': { opacity: 0, transform: 'translateX(-4px)' },
                                '100%': { opacity: 1, transform: 'translateX(0)' },
                              },
                            }}
                          >
                            <Typography
                              className="peek-name"
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.825rem',
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: 'text.primary',
                                transition: 'color 160ms ease',
                              }}
                            >
                              {niceName(slug)}
                            </Typography>
                            <ChevronRightIcon
                              className="peek-arrow"
                              sx={{
                                fontSize: 15,
                                color: accent,
                                opacity: 0,
                                transform: 'translateX(-3px)',
                                transition: 'opacity 160ms ease, transform 160ms ease',
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── HEX (Excel Tools) — honeycomb grid of hexagonal tiles ──
          //   Hexagons tessellate by offsetting every other row by half a
          //   cell. Each hex glows green on hover and scales up; tool name
          //   sits centered. Grid uses CSS clip-path for the hex shape so
          //   no SVG / canvas is needed.
          if (variant === 'hex') {
            // For perfect tessellation we render rows with 3 / 2 / 3 / 2
            // alternating, the 2-row offset half a cell to the right.
            const HEX_PER_ODD = 3;
            const HEX_PER_EVEN = 2;
            const rows = [];
            let idx = 0;
            while (idx < accessibleSlugs.length) {
              const odd = rows.length % 2 === 0;
              const take = odd ? HEX_PER_ODD : HEX_PER_EVEN;
              rows.push(accessibleSlugs.slice(idx, idx + take));
              idx += take;
            }
            const hexShape = 'polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0% 50%)';
            return (
              <Fade {...TransitionProps} timeout={{ enter: 260, exit: 140 }}>
                <Paper elevation={10} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 360, maxHeight: 460, borderRadius: 2,
                    border: `1px solid ${hoverCategoryData.color}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={0.5}>
                        {rows.map((rowSlugs, rIdx) => (
                          <Box
                            key={rIdx}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: 0.75,
                              // Offset every-other-row so hexes tessellate.
                              ml: rIdx % 2 === 1 ? 4.5 : 0,
                              mr: rIdx % 2 === 1 ? 4.5 : 0,
                              // Pull rows together to overlap hex points.
                              mt: rIdx === 0 ? 0 : -1,
                            }}
                          >
                            {rowSlugs.map((slug, cIdx) => {
                              const i = rows.slice(0, rIdx).reduce((a, r) => a + r.length, 0) + cIdx;
                              return (
                                <Box key={slug} onClick={goTo(slug)}
                                  sx={{
                                    width: 90, height: 90,
                                    clipPath: hexShape,
                                    bgcolor: `${hoverCategoryData.color}20`,
                                    cursor: 'pointer', position: 'relative',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    textAlign: 'center', overflow: 'hidden',
                                    opacity: 0, transform: 'scale(0.7)',
                                    animation: 'peekHexIn 320ms cubic-bezier(0.34,1.56,0.64,1) forwards',
                                    animationDelay: `${i * 40}ms`,
                                    transition: 'transform 220ms, background-color 220ms, filter 220ms',
                                    '&::before': {
                                      // Inner hex outline by clipping a slightly smaller hex
                                      content: '""', position: 'absolute', inset: 2,
                                      clipPath: hexShape,
                                      bgcolor: isDark ? 'rgba(20,28,22,0.92)' : 'rgba(252,255,252,0.96)',
                                      transition: 'background-color 220ms',
                                    },
                                    '&:hover': {
                                      bgcolor: hoverCategoryData.color,
                                      transform: 'scale(1.1)',
                                      filter: `drop-shadow(0 8px 20px ${hoverCategoryData.color}80)`,
                                      '&::before': {
                                        bgcolor: `${hoverCategoryData.color}25`,
                                      },
                                    },
                                    '@keyframes peekHexIn': {
                                      '0%':   { opacity: 0, transform: 'scale(0.7) rotate(-12deg)' },
                                      '100%': { opacity: 1, transform: 'scale(1) rotate(0)' },
                                    },
                                  }}>
                                  <Typography variant="caption" sx={{
                                    position: 'relative', zIndex: 1,
                                    fontWeight: 700, fontSize: 10, lineHeight: 1.15,
                                    px: 1, color: 'text.primary',
                                    display: '-webkit-box', WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                  }}>
                                    {niceName(slug)}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── SHEET (Excel Tools) — looks like an actual spreadsheet ──
          //   Tools live in cells with row numbers down the left and a
          //   "Tool" column header across the top, like opening Excel and
          //   seeing your shortcuts as data. Hovering a row highlights it
          //   with the Excel-green selection accent.
          if (variant === 'sheet') {
            return (
              <Fade {...TransitionProps} timeout={{ enter: 220, exit: 140 }}>
                <Paper elevation={10} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 340, maxHeight: 420, borderRadius: 1,
                    border: `1px solid ${hoverCategoryData.color}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  }}>
                  {headerCommon}
                  {/* Spreadsheet column header strip — fixed under main header */}
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: '36px 1fr',
                    borderBottom: `1px solid ${hoverCategoryData.color}80`,
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#f7f9f7',
                  }}>
                    <Box sx={{
                      borderRight: `1px solid ${theme.palette.divider}`,
                      py: 0.5, textAlign: 'center',
                    }}>
                      <Typography variant="caption" sx={{
                        fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                        color: 'text.secondary',
                      }}>#</Typography>
                    </Box>
                    <Box sx={{ py: 0.5, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{
                        fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                        color: 'text.secondary',
                      }}>A &nbsp;·&nbsp; Tool</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto', ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Box sx={{
                        // Subtle gridlines on the body — like Excel cell
                        // gridlines bleeding through.
                        backgroundImage: `linear-gradient(to bottom, transparent 0%, transparent calc(100% - 1px), ${theme.palette.divider} calc(100% - 1px))`,
                        backgroundSize: '100% 32px',
                      }}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box key={slug} onClick={goTo(slug)}
                            sx={{
                              display: 'grid', gridTemplateColumns: '36px 1fr',
                              cursor: 'pointer', minHeight: 32,
                              opacity: 0,
                              animation: 'peekSheetIn 220ms ease-out forwards',
                              animationDelay: `${i * 22}ms`,
                              transition: 'background-color 140ms',
                              '&:hover': {
                                bgcolor: `${hoverCategoryData.color}18`,
                                '& .row-number': { color: hoverCategoryData.color },
                                '& .cell-content': { fontWeight: 700 },
                              },
                              '@keyframes peekSheetIn': {
                                '0%': { opacity: 0 },
                                '100%': { opacity: 1 },
                              },
                            }}>
                            <Box className="row-number" sx={{
                              borderRight: `1px solid ${theme.palette.divider}`,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#f0f3f0',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'color 140ms, background-color 140ms',
                            }}>
                              <Typography variant="caption" sx={{
                                fontFamily: 'monospace', fontSize: 11,
                                color: 'text.secondary',
                              }}>{i + 1}</Typography>
                            </Box>
                            <Box sx={{
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              display: 'flex', alignItems: 'center', px: 1.25,
                            }}>
                              <Typography className="cell-content" variant="body2" sx={{
                                fontWeight: 600, fontSize: 13,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                transition: 'font-weight 140ms',
                              }}>
                                {niceName(slug)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // (legacy bento — kept reachable via peekVariant: 'bento' if needed)
          if (variant === 'bento') {
            const [hero, ...rest] = accessibleSlugs;
            return (
              <Fade {...TransitionProps} timeout={{ enter: 280, exit: 160 }}>
                <Paper elevation={12} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 380, maxHeight: 480, borderRadius: 2.5,
                    border: `1px solid ${hoverCategoryData.color}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={1.25}>
                        {hero && (
                          <Box onClick={goTo(hero)}
                            sx={{
                              position: 'relative', overflow: 'hidden', borderRadius: 2, cursor: 'pointer',
                              p: 2.5, minHeight: 96,
                              border: `1px solid ${hoverCategoryData.color}50`,
                              background: `linear-gradient(135deg, ${hoverCategoryData.color}38 0%, ${hoverCategoryData.color}10 60%, transparent 100%)`,
                              opacity: 0, transform: 'translateY(-12px) scale(0.95)',
                              animation: 'peekBentoHeroIn 460ms cubic-bezier(0.16,1,0.3,1) forwards',
                              transition: 'transform 280ms cubic-bezier(0.16,1,0.3,1), box-shadow 280ms',
                              '&::before': {
                                content: '""', position: 'absolute', inset: 0,
                                background: `linear-gradient(115deg, transparent 30%, ${hoverCategoryData.color}25 50%, transparent 70%)`,
                                backgroundSize: '300% 100%', backgroundPosition: '100% 0',
                                animation: 'peekBentoSheen 4s ease-in-out infinite',
                                pointerEvents: 'none',
                              },
                              '&::after': {
                                content: '""', position: 'absolute',
                                top: 12, right: 12, width: 8, height: 8, borderRadius: '50%',
                                bgcolor: hoverCategoryData.color,
                                boxShadow: `0 0 14px ${hoverCategoryData.color}, 0 0 0 4px ${hoverCategoryData.color}30`,
                              },
                              '&:hover': { transform: 'translateY(-3px) scale(1.01)', boxShadow: `0 14px 36px ${hoverCategoryData.color}40` },
                              '@keyframes peekBentoHeroIn': {
                                '0%': { opacity: 0, transform: 'translateY(-12px) scale(0.95)' },
                                '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
                              },
                              '@keyframes peekBentoSheen': {
                                '0%': { backgroundPosition: '100% 0' },
                                '100%': { backgroundPosition: '-100% 0' },
                              },
                            }}>
                            <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
                              <Typography variant="caption" sx={{
                                fontWeight: 700, letterSpacing: 1, color: hoverCategoryData.color,
                                textTransform: 'uppercase', fontSize: 10,
                              }}>Featured</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>{niceName(hero)}</Typography>
                              <Typography variant="caption" color="text.secondary">Open this tool now</Typography>
                            </Stack>
                          </Box>
                        )}
                        {rest.length > 0 && (
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            {rest.map((slug, i) => (
                              <Box key={slug} onClick={goTo(slug)}
                                sx={{
                                  position: 'relative', overflow: 'hidden',
                                  p: 1.25, borderRadius: 1.5, cursor: 'pointer',
                                  border: `1px solid ${theme.palette.divider}`,
                                  background: isDark
                                    ? `linear-gradient(135deg, ${hoverCategoryData.color}15 0%, transparent 80%)`
                                    : `linear-gradient(135deg, ${hoverCategoryData.color}10 0%, transparent 80%)`,
                                  minHeight: 60, display: 'flex', alignItems: 'flex-end',
                                  opacity: 0, transform: 'translateY(8px) scale(0.95)',
                                  animation: 'peekBentoItemIn 380ms cubic-bezier(0.16,1,0.3,1) forwards',
                                  animationDelay: `${120 + i * 60}ms`,
                                  transition: 'border-color 220ms, transform 220ms, box-shadow 240ms, background 220ms',
                                  '&::before': {
                                    content: '""', position: 'absolute',
                                    top: 8, right: 8, width: 4, height: 4, borderRadius: '50%',
                                    bgcolor: hoverCategoryData.color, opacity: 0.5,
                                    transition: 'opacity 220ms, transform 220ms, box-shadow 220ms',
                                  },
                                  '&:hover': {
                                    borderColor: hoverCategoryData.color,
                                    transform: 'translateY(-3px)',
                                    boxShadow: `0 8px 22px ${hoverCategoryData.color}30`,
                                    background: `linear-gradient(135deg, ${hoverCategoryData.color}30 0%, ${hoverCategoryData.color}05 100%)`,
                                    '&::before': { opacity: 1, transform: 'scale(1.6)', boxShadow: `0 0 10px ${hoverCategoryData.color}` },
                                  },
                                  '@keyframes peekBentoItemIn': {
                                    '0%': { opacity: 0, transform: 'translateY(8px) scale(0.95)' },
                                    '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
                                  },
                                }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2,
                                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                  {niceName(slug)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── MINIMAL (Link) — typographic clean list ──
          if (variant === 'minimal') {
            return (
              <Fade {...TransitionProps} timeout={{ enter: 240, exit: 140 }}>
                <Paper elevation={4} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 300, maxHeight: 380, borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', py: 1.25, px: 0.5, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={0}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box key={slug} onClick={goTo(slug)}
                            sx={{
                              px: 2, py: 1.1, cursor: 'pointer', position: 'relative',
                              textAlign: 'center',
                              opacity: 0, animation: 'peekMinimalIn 240ms ease-out forwards',
                              animationDelay: `${i * 22}ms`,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              '&:last-child': { borderBottom: 'none' },
                              '& .peek-name': { position: 'relative', display: 'inline-block', transition: 'color 200ms' },
                              '& .peek-name::after': {
                                content: '""', position: 'absolute',
                                left: 0, bottom: -2, height: 2, width: 0,
                                bgcolor: hoverCategoryData.color,
                                transition: 'width 240ms cubic-bezier(0.16,1,0.3,1)',
                              },
                              '&:hover .peek-name': { color: hoverCategoryData.color },
                              '&:hover .peek-name::after': { width: '100%' },
                              '@keyframes peekMinimalIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } },
                            }}>
                            <Typography component="span" variant="body2" className="peek-name" sx={{ fontWeight: 600 }}>
                              {niceName(slug)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── MOSAIC (Other Tools) — irregular tile grid ──
          if (variant === 'mosaic') {
            return (
              <Fade {...TransitionProps} timeout={{ enter: 240, exit: 140 }}>
                <Paper elevation={10} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 360, maxHeight: 420, borderRadius: 2,
                    border: `1px solid ${hoverCategoryData.color}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Box sx={{
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                        gridAutoRows: 'minmax(64px, auto)', gap: 0.75,
                      }}>
                        {accessibleSlugs.map((slug, i) => {
                          const isWide = (i + 1) % 5 === 0;
                          return (
                            <Box key={slug} onClick={goTo(slug)}
                              sx={{
                                gridColumn: isWide ? 'span 2' : 'span 1',
                                position: 'relative', p: 1.25, borderRadius: 1.5, cursor: 'pointer',
                                bgcolor: `${hoverCategoryData.color}10`,
                                border: `1px solid ${hoverCategoryData.color}30`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                textAlign: 'center', overflow: 'hidden',
                                opacity: 0, transform: 'scale(0.85)',
                                animation: 'peekMosaicIn 360ms cubic-bezier(0.16,1,0.3,1) forwards',
                                animationDelay: `${Math.min(i, 12) * 35}ms`,
                                transition: 'background-color 200ms, color 200ms, transform 200ms, box-shadow 220ms',
                                '&::after': {
                                  content: '""', position: 'absolute', inset: 0,
                                  background: `radial-gradient(circle at 50% 0%, ${hoverCategoryData.color}40, transparent 70%)`,
                                  opacity: 0, transition: 'opacity 220ms', pointerEvents: 'none',
                                },
                                '&:hover': {
                                  bgcolor: hoverCategoryData.color,
                                  color: theme.palette.getContrastText(hoverCategoryData.color),
                                  transform: 'translateY(-3px) scale(1.02)',
                                  boxShadow: `0 8px 20px ${hoverCategoryData.color}40`,
                                  '&::after': { opacity: 1 },
                                },
                                '@keyframes peekMosaicIn': {
                                  '0%': { opacity: 0, transform: 'scale(0.85)' },
                                  '100%': { opacity: 1, transform: 'scale(1)' },
                                },
                              }}>
                              <Typography variant="body2" sx={{
                                fontWeight: 600, lineHeight: 1.2, position: 'relative', zIndex: 1,
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}>{niceName(slug)}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── ORBIT (AI Tools) — items orbit a glowing core ──
          //   Tools sit on circular orbits around a pulsing AI core.
          //   The whole orbit container slowly rotates; on hover an item
          //   the rotation pauses and that planet scales up + glows.
          //   Items are positioned with absolute coords on a calculated
          //   angle/radius, distributed evenly around the core.
          if (variant === 'orbit') {
            const radii = [60, 95, 130];  // up to 3 concentric orbits
            const itemsByOrbit = [];
            // Distribute: up to 4 on inner, 6 on middle, rest on outer.
            const capacities = [4, 6, 12];
            let cursor = 0;
            for (let r = 0; r < radii.length && cursor < accessibleSlugs.length; r++) {
              const slice = accessibleSlugs.slice(cursor, cursor + capacities[r]);
              itemsByOrbit.push(slice);
              cursor += slice.length;
            }
            // Anything left after orbit 3 → squish into outer orbit.
            if (cursor < accessibleSlugs.length) {
              itemsByOrbit[itemsByOrbit.length - 1] = [
                ...itemsByOrbit[itemsByOrbit.length - 1],
                ...accessibleSlugs.slice(cursor),
              ];
            }
            return (
              <Fade {...TransitionProps} timeout={{ enter: 320, exit: 160 }}>
                <Paper elevation={10} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 360, height: 380, borderRadius: 3,
                    border: `1px solid ${hoverCategoryData.color}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    bgcolor: isDark ? 'rgba(10,8,14,0.96)' : 'rgba(254,250,255,0.96)',
                    backgroundImage: `radial-gradient(circle at 50% 60%, ${hoverCategoryData.color}25 0%, transparent 50%)`,
                  }}>
                  {headerCommon}
                  <Box sx={{
                    flex: 1, position: 'relative', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {/* Faint orbit rings as visual guides */}
                    {radii.slice(0, itemsByOrbit.length).map((r, rIdx) => (
                      <Box key={rIdx} sx={{
                        position: 'absolute',
                        width: r * 2, height: r * 2,
                        borderRadius: '50%',
                        border: `1px dashed ${hoverCategoryData.color}40`,
                      }} />
                    ))}

                    {/* Glowing AI core in the center */}
                    <Box sx={{
                      position: 'absolute',
                      width: 56, height: 56, borderRadius: '50%',
                      bgcolor: hoverCategoryData.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 0 30px ${hoverCategoryData.color}, 0 0 60px ${hoverCategoryData.color}80, 0 0 90px ${hoverCategoryData.color}60`,
                      animation: 'peekOrbitCorePulse 2.6s ease-in-out infinite',
                      zIndex: 2,
                      '@keyframes peekOrbitCorePulse': {
                        '0%, 100%': { transform: 'scale(1)', boxShadow: `0 0 26px ${hoverCategoryData.color}, 0 0 52px ${hoverCategoryData.color}80` },
                        '50%':      { transform: 'scale(1.12)', boxShadow: `0 0 40px ${hoverCategoryData.color}, 0 0 80px ${hoverCategoryData.color}90, 0 0 120px ${hoverCategoryData.color}50` },
                      },
                    }}>
                      <hoverCategoryData.icon size={26} color={theme.palette.getContrastText(hoverCategoryData.color)} />
                    </Box>

                    {/* Rotating orbit container — child items counter-
                        rotate so their text stays upright. */}
                    <Box
                      className="orbit-rotator"
                      sx={{
                        position: 'absolute',
                        width: '100%', height: '100%',
                        animation: 'peekOrbitSpin 24s linear infinite',
                        '&:hover': { animationPlayState: 'paused' },
                        '@keyframes peekOrbitSpin': {
                          '0%':   { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                        '@keyframes peekOrbitCounterSpin': {
                          '0%':   { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(-360deg)' },
                        },
                        '@keyframes peekOrbitPlanetIn': {
                          '0%':   { opacity: 0, transform: 'scale(0)' },
                          '100%': { opacity: 1, transform: 'scale(1)' },
                        },
                      }}
                    >
                      {itemsByOrbit.flatMap((orbitSlugs, orbitIdx) => {
                        const r = radii[orbitIdx];
                        const n = orbitSlugs.length;
                        return orbitSlugs.map((slug, i) => {
                          const angle = (360 / n) * i;
                          const flatIdx = itemsByOrbit.slice(0, orbitIdx).reduce((a, x) => a + x.length, 0) + i;
                          return (
                            <Box key={slug}
                              onClick={goTo(slug)}
                              sx={{
                                position: 'absolute',
                                top: '50%', left: '50%',
                                transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-${r}px)`,
                                opacity: 0,
                                animation: `peekOrbitPlanetIn 360ms cubic-bezier(0.34,1.56,0.64,1) forwards ${flatIdx * 50}ms`,
                              }}>
                              {/* Counter-rotate inner so text reads upright. */}
                              <Box
                                sx={{
                                  transform: `rotate(${-angle}deg)`,
                                  // The counter-spin matches the parent
                                  // orbit speed so the planet orientation
                                  // stays upright as the orbit rotates.
                                  animation: 'peekOrbitCounterSpin 24s linear infinite',
                                }}
                              >
                                <Box sx={{
                                  px: 1.25, py: 0.6, borderRadius: 999,
                                  cursor: 'pointer',
                                  bgcolor: `${hoverCategoryData.color}30`,
                                  border: `1px solid ${hoverCategoryData.color}60`,
                                  color: 'text.primary',
                                  whiteSpace: 'nowrap',
                                  fontWeight: 600,
                                  transition: 'transform 220ms, background-color 220ms, box-shadow 220ms',
                                  boxShadow: `0 0 0 0 ${hoverCategoryData.color}`,
                                  '&:hover': {
                                    transform: 'scale(1.18)',
                                    bgcolor: hoverCategoryData.color,
                                    color: theme.palette.getContrastText(hoverCategoryData.color),
                                    boxShadow: `0 0 14px ${hoverCategoryData.color}, 0 0 30px ${hoverCategoryData.color}80`,
                                  },
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>
                                    {niceName(slug)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          );
                        });
                      })}
                    </Box>
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── HOLO (AI Tools) — iridescent holographic shimmer ──
          //   Each item card has an animated rainbow gradient that drifts
          //   across its surface; on hover the rainbow accelerates and the
          //   card lifts. Background-clip on the title creates a shimmering
          //   text effect. AI = generative = rainbow, basically.
          if (variant === 'holo') {
            return (
              <Fade {...TransitionProps} timeout={{ enter: 280, exit: 160 }}>
                <Paper elevation={0} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 340, maxHeight: 420, borderRadius: 3,
                    border: `1px solid ${hoverCategoryData.color}55`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    bgcolor: isDark ? 'rgba(15,15,20,0.96)' : 'rgba(250,250,255,0.96)',
                    boxShadow: `0 16px 48px rgba(0,0,0,0.25)`,
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={1}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box key={slug} onClick={goTo(slug)}
                            sx={{
                              position: 'relative', overflow: 'hidden',
                              p: 1.5, borderRadius: 2, cursor: 'pointer',
                              border: `1px solid transparent`,
                              backgroundColor: isDark ? 'rgba(20,20,28,0.85)' : 'rgba(255,255,255,0.85)',
                              opacity: 0, transform: 'translateY(8px)',
                              animation: 'peekHoloIn 380ms cubic-bezier(0.16,1,0.3,1) forwards',
                              animationDelay: `${i * 50}ms`,
                              transition: 'transform 220ms, box-shadow 220ms, border-color 220ms',
                              // Iridescent rainbow shimmer behind everything.
                              // Slow gentle drift by default, accelerates on hover.
                              '&::before': {
                                content: '""', position: 'absolute', inset: 0,
                                background: `linear-gradient(115deg,
                                  #ff0080 0%,
                                  #ff8c00 20%,
                                  #ffd000 40%,
                                  #00d8ff 60%,
                                  #b400ff 80%,
                                  #ff0080 100%)`,
                                backgroundSize: '300% 100%',
                                backgroundPosition: '0% 0%',
                                opacity: 0.18,
                                animation: 'peekHoloShimmer 9s linear infinite',
                                pointerEvents: 'none',
                                transition: 'opacity 220ms',
                              },
                              // Outer glow halo on hover.
                              '&::after': {
                                content: '""', position: 'absolute', inset: -1,
                                borderRadius: 'inherit',
                                background: `linear-gradient(115deg, #ff0080, #00d8ff, #ffd000, #b400ff, #ff0080)`,
                                backgroundSize: '300% 100%', backgroundPosition: '0% 0%',
                                animation: 'peekHoloShimmer 9s linear infinite',
                                opacity: 0, transition: 'opacity 280ms',
                                zIndex: -1, pointerEvents: 'none',
                                filter: 'blur(10px)',
                              },
                              '&:hover': {
                                transform: 'translateY(-2px) scale(1.015)',
                                borderColor: 'transparent',
                                boxShadow: '0 12px 28px rgba(255,0,128,0.25), 0 0 0 1px rgba(255,255,255,0.08)',
                                '&::before': {
                                  opacity: 0.35,
                                  animation: 'peekHoloShimmer 3.5s linear infinite',
                                },
                                '&::after': { opacity: 0.6 },
                              },
                              '@keyframes peekHoloIn': {
                                '0%': { opacity: 0, transform: 'translateY(8px)' },
                                '100%': { opacity: 1, transform: 'translateY(0)' },
                              },
                              '@keyframes peekHoloShimmer': {
                                '0%':   { backgroundPosition: '0% 0%' },
                                '100%': { backgroundPosition: '300% 0%' },
                              },
                            }}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
                              <Box sx={{
                                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                background: `conic-gradient(from 0deg, #ff0080, #ff8c00, #ffd000, #00d8ff, #b400ff, #ff0080)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 0 14px rgba(0,216,255,0.5), 0 0 24px rgba(255,0,128,0.3)',
                              }}>
                                <Box sx={{
                                  width: 28, height: 28, borderRadius: '50%',
                                  bgcolor: isDark ? '#0f0f14' : '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 13, fontWeight: 800,
                                  background: `linear-gradient(135deg, #ff0080 0%, #00d8ff 100%)`,
                                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                }}>
                                  {niceName(slug).charAt(0)}
                                </Box>
                              </Box>
                              <Typography variant="body2" sx={{
                                flex: 1, minWidth: 0, fontWeight: 700,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                color: 'text.primary',
                              }}>
                                {niceName(slug)}
                              </Typography>
                              <ChevronRightIcon sx={{
                                fontSize: 18,
                                color: hoverCategoryData.color,
                                filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.4))',
                              }} />
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // (legacy glass — kept reachable via peekVariant: 'glass' if needed)
          if (variant === 'glass') {
            return (
              <Fade {...TransitionProps} timeout={{ enter: 280, exit: 160 }}>
                <Paper elevation={0} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 340, maxHeight: 420, borderRadius: 3,
                    border: `1px solid ${hoverCategoryData.color}55`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    backgroundColor: isDark ? 'rgba(20,20,28,0.78)' : 'rgba(255,255,255,0.78)',
                    backdropFilter: 'blur(14px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(14px) saturate(160%)',
                    boxShadow: `0 12px 48px ${hoverCategoryData.color}25, 0 0 0 1px ${hoverCategoryData.color}20`,
                    backgroundImage: `radial-gradient(ellipse at top, ${hoverCategoryData.color}25 0%, transparent 70%)`,
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.25, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={0.75}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box key={slug} onClick={goTo(slug)}
                            sx={{
                              p: 1.25, borderRadius: 2, cursor: 'pointer',
                              border: `1px solid ${theme.palette.divider}`,
                              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.5)',
                              display: 'flex', alignItems: 'center', gap: 1.25,
                              opacity: 0, transform: 'translateY(8px)',
                              animation: 'peekGlassIn 360ms cubic-bezier(0.16,1,0.3,1) forwards',
                              animationDelay: `${i * 40}ms`,
                              transition: 'transform 220ms, border-color 220ms, box-shadow 220ms, background-color 220ms',
                              '&:hover': {
                                transform: 'translateY(-2px) scale(1.02)',
                                borderColor: hoverCategoryData.color,
                                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)',
                                boxShadow: `0 6px 18px ${hoverCategoryData.color}40, 0 0 0 1px ${hoverCategoryData.color}40`,
                              },
                              '@keyframes peekGlassIn': {
                                '0%': { opacity: 0, transform: 'translateY(8px)' },
                                '100%': { opacity: 1, transform: 'translateY(0)' },
                              },
                            }}>
                            <Avatar sx={{
                              width: 36, height: 36, fontSize: 15, fontWeight: 700,
                              bgcolor: `${hoverCategoryData.color}30`, color: hoverCategoryData.color,
                              boxShadow: `0 0 16px ${hoverCategoryData.color}40`,
                            }}>{niceName(slug).charAt(0)}</Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {niceName(slug)}
                              </Typography>
                            </Box>
                            <ChevronRightIcon sx={{ fontSize: 18, color: hoverCategoryData.color, opacity: 0.6 }} />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── CARDS (Reports) — Spotify-style album cover grid ──
          //   Square cards in a 2-column grid, each with its own deep
          //   gradient cover (hue rotated per index off the category
          //   color), big bold title at the bottom. Hover scales the
          //   cover slightly and animates the gradient direction —
          //   feels like browsing a music app.
          if (variant === 'cards') {
            // Hue-shift helper: rotate the category color around the wheel
            // by an offset and re-lighten/darken slightly per index.
            const hexToHsl = (hex) => {
              const r = parseInt(hex.slice(1, 3), 16) / 255;
              const g = parseInt(hex.slice(3, 5), 16) / 255;
              const b = parseInt(hex.slice(5, 7), 16) / 255;
              const max = Math.max(r, g, b), min = Math.min(r, g, b);
              let h = 0, s = 0; const l = (max + min) / 2;
              if (max !== min) {
                const d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                  case r: h = ((g - b) / d) + (g < b ? 6 : 0); break;
                  case g: h = ((b - r) / d) + 2; break;
                  case b: h = ((r - g) / d) + 4; break;
                  default: break;
                }
                h *= 60;
              }
              return [h, s * 100, l * 100];
            };
            const baseHsl = hexToHsl(hoverCategoryData.color);
            const coverGradient = (i) => {
              const h1 = (baseHsl[0] + i * 25) % 360;
              const h2 = (baseHsl[0] + i * 25 + 50) % 360;
              return `linear-gradient(135deg, hsl(${h1}, 70%, 35%) 0%, hsl(${h2}, 65%, 22%) 100%)`;
            };
            return (
              <Fade {...TransitionProps} timeout={{ enter: 280, exit: 160 }}>
                <Paper elevation={10} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 360, maxHeight: 460, borderRadius: 2.5,
                    border: `1px solid ${hoverCategoryData.color}`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box key={slug} onClick={goTo(slug)}
                            sx={{
                              position: 'relative', aspectRatio: '1 / 1',
                              borderRadius: 1.5, cursor: 'pointer', overflow: 'hidden',
                              opacity: 0, transform: 'scale(0.92)',
                              animation: 'peekCardsIn 380ms cubic-bezier(0.16,1,0.3,1) forwards',
                              animationDelay: `${i * 55}ms`,
                              transition: 'transform 280ms cubic-bezier(0.16,1,0.3,1), box-shadow 280ms',
                              '&:hover': {
                                transform: 'scale(1.04)',
                                boxShadow: `0 14px 30px rgba(0,0,0,0.45)`,
                                '& .cover-gradient': {
                                  transform: 'rotate(8deg) scale(1.15)',
                                },
                                '& .cover-shine': { opacity: 1 },
                                '& .cover-icon': { transform: 'translateY(-4px) scale(1.15)' },
                              },
                              '@keyframes peekCardsIn': {
                                '0%':   { opacity: 0, transform: 'scale(0.92)' },
                                '100%': { opacity: 1, transform: 'scale(1)' },
                              },
                            }}>
                            {/* Gradient "album cover" */}
                            <Box className="cover-gradient" sx={{
                              position: 'absolute', inset: -10,
                              background: coverGradient(i),
                              transition: 'transform 320ms cubic-bezier(0.16,1,0.3,1)',
                            }} />
                            {/* Shine overlay — diagonal sheen on hover */}
                            <Box className="cover-shine" sx={{
                              position: 'absolute', inset: 0,
                              background: 'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.25) 50%, transparent 65%)',
                              opacity: 0, transition: 'opacity 320ms',
                              pointerEvents: 'none',
                            }} />
                            {/* Big monogram icon mid-card */}
                            <Box className="cover-icon" sx={{
                              position: 'absolute', top: '32%', left: '50%',
                              transform: 'translate(-50%, -50%)',
                              fontSize: 38, fontWeight: 900,
                              color: 'rgba(255,255,255,0.5)',
                              fontFamily: 'monospace',
                              transition: 'transform 280ms',
                              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
                            }}>
                              {niceName(slug).charAt(0)}
                            </Box>
                            {/* Title + meta strip at the bottom */}
                            <Box sx={{
                              position: 'absolute', left: 0, right: 0, bottom: 0,
                              p: 1, pt: 2,
                              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.6) 100%)',
                            }}>
                              <Typography variant="caption" sx={{
                                color: 'rgba(255,255,255,0.7)',
                                textTransform: 'uppercase', letterSpacing: 1, fontSize: 9, fontWeight: 700,
                                display: 'block',
                              }}>
                                {hoverCategoryData.title}
                              </Typography>
                              <Typography variant="body2" sx={{
                                color: 'white', fontWeight: 800, lineHeight: 1.2, fontSize: 12,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {niceName(slug)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // ── CONSTELLATION (Reports) — items as twinkling stars ──
          //   Items are arranged with subtle pseudo-random horizontal
          //   offsets (deterministic by index) and connected by a faint
          //   zigzag line, like dots on a star map. Each "star" pulses
          //   on its own rhythm. Hover: star expands and ignites with a
          //   bright halo. Items are still left-anchored enough to be
          //   readable; the offsets just give the panel an organic feel.
          if (variant === 'constellation') {
            // Pseudo-random offsets in pixels — derived from index so the
            // pattern is stable across re-renders.
            const xOffsetFor = (i) => {
              const seed = (i * 9301 + 49297) % 233280;
              return ((seed / 233280) * 38) - 8;  // -8px to +30px
            };
            return (
              <Fade {...TransitionProps} timeout={{ enter: 280, exit: 160 }}>
                <Paper elevation={10} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 340, maxHeight: 460, borderRadius: 2.5,
                    border: `1px solid ${hoverCategoryData.color}66`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    bgcolor: isDark ? 'rgba(8,12,20,0.96)' : 'rgba(245,250,255,0.96)',
                    // Subtle starfield background — many tiny dim dots.
                    backgroundImage: isDark
                      ? `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.06) 1px, transparent 1px),
                         radial-gradient(circle at 70% 60%, rgba(255,255,255,0.04) 1px, transparent 1px),
                         radial-gradient(circle at 40% 80%, rgba(255,255,255,0.05) 1px, transparent 1px),
                         radial-gradient(circle at 85% 25%, rgba(255,255,255,0.04) 1px, transparent 1px)`
                      : `radial-gradient(circle at 20% 30%, rgba(0,30,60,0.05) 1px, transparent 1px),
                         radial-gradient(circle at 70% 60%, rgba(0,30,60,0.04) 1px, transparent 1px),
                         radial-gradient(circle at 40% 80%, rgba(0,30,60,0.04) 1px, transparent 1px),
                         radial-gradient(circle at 85% 25%, rgba(0,30,60,0.03) 1px, transparent 1px)`,
                    backgroundSize: '120px 120px, 80px 80px, 200px 200px, 60px 60px',
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative', py: 2, px: 1.5, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={1.75} sx={{ position: 'relative' }}>
                        {accessibleSlugs.map((slug, i) => {
                          const dx = xOffsetFor(i);
                          const dxNext = i < accessibleSlugs.length - 1 ? xOffsetFor(i + 1) : dx;
                          const pulseDur = 2.4 + ((i * 0.7) % 2);  // 2.4 - 4.4s
                          return (
                            <Box key={slug} onClick={goTo(slug)}
                              sx={{
                                position: 'relative',
                                ml: `${dx}px`,
                                p: 1.25, pl: 4, borderRadius: 2, cursor: 'pointer',
                                opacity: 0,
                                animation: 'peekConstIn 420ms cubic-bezier(0.16,1,0.3,1) forwards',
                                animationDelay: `${i * 80}ms`,
                                transition: 'background-color 220ms, transform 220ms',
                                // The "star" — pulsing dot on the left.
                                '&::before': {
                                  content: '""', position: 'absolute',
                                  left: 8, top: '50%', transform: 'translateY(-50%)',
                                  width: 10, height: 10, borderRadius: '50%',
                                  bgcolor: hoverCategoryData.color,
                                  boxShadow: `0 0 8px ${hoverCategoryData.color}, 0 0 16px ${hoverCategoryData.color}80`,
                                  animation: `peekConstPulse ${pulseDur}s ease-in-out infinite`,
                                  zIndex: 2,
                                  transition: 'transform 220ms, box-shadow 220ms',
                                },
                                // Connector line to the next item — drawn from
                                // the star center down to the next star's
                                // approximate position. Has a subtle slant
                                // matching the next item's offset.
                                '&::after': i < accessibleSlugs.length - 1 ? {
                                  content: '""', position: 'absolute',
                                  left: 13, top: 'calc(50% + 5px)',
                                  width: 1, height: 'calc(100% + 14px)',
                                  bgcolor: `${hoverCategoryData.color}40`,
                                  transform: `skewX(${(dxNext - dx) * -0.6}deg) translateX(${(dxNext - dx) * 0.5}px)`,
                                  transformOrigin: 'top center',
                                  zIndex: 1, pointerEvents: 'none',
                                } : {},
                                '&:hover': {
                                  bgcolor: `${hoverCategoryData.color}14`,
                                  transform: 'translateX(2px)',
                                  '&::before': {
                                    transform: 'translateY(-50%) scale(1.6)',
                                    boxShadow: `0 0 16px ${hoverCategoryData.color}, 0 0 32px ${hoverCategoryData.color}, 0 0 48px ${hoverCategoryData.color}80`,
                                  },
                                },
                                '@keyframes peekConstIn': {
                                  '0%': { opacity: 0, transform: `translateX(${dx > 0 ? -10 : 10}px)` },
                                  '100%': { opacity: 1, transform: 'translateX(0)' },
                                },
                                '@keyframes peekConstPulse': {
                                  '0%, 100%': { opacity: 0.7, filter: 'brightness(1)' },
                                  '50%':      { opacity: 1, filter: 'brightness(1.4)' },
                                },
                              }}>
                              <Typography variant="body2" sx={{
                                fontWeight: 600, position: 'relative', zIndex: 2,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {niceName(slug)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          // (legacy aurora — kept reachable via peekVariant: 'aurora' if needed)
          if (variant === 'aurora') {
            return (
              <Fade {...TransitionProps} timeout={{ enter: 320, exit: 160 }}>
                <Paper elevation={0} onMouseEnter={cancelPeekClose} onMouseLeave={scheduleCategoryClose}
                  sx={{
                    width: 360, maxHeight: 460, borderRadius: 3,
                    border: `1px solid ${hoverCategoryData.color}66`,
                    overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    bgcolor: isDark ? 'rgba(15,18,23,0.92)' : 'rgba(255,255,255,0.92)',
                    boxShadow: `0 20px 60px ${hoverCategoryData.color}30, 0 0 0 1px ${hoverCategoryData.color}25`,
                  }}>
                  {headerCommon}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.75, ...customScrollbar }}>
                    {accessibleSlugs.length === 0 ? emptyState : (
                      <Stack spacing={1.5}>
                        {accessibleSlugs.map((slug, i) => (
                          <Box key={slug} onClick={goTo(slug)}
                            sx={{
                              position: 'relative', overflow: 'hidden', borderRadius: 2.5, cursor: 'pointer',
                              p: 2.5, minHeight: 120,
                              border: `1px solid ${hoverCategoryData.color}40`,
                              backgroundColor: isDark ? '#0f1419' : '#fafdfc',
                              opacity: 0, transform: 'scale(0.92) translateY(12px)',
                              animation: 'peekAuroraIn 540ms cubic-bezier(0.16,1,0.3,1) forwards',
                              animationDelay: `${i * 100}ms`,
                              transition: 'transform 280ms cubic-bezier(0.16,1,0.3,1), box-shadow 280ms',
                              '&::before': {
                                content: '""', position: 'absolute', inset: 0,
                                background: `
                                  radial-gradient(ellipse 60% 50% at 20% 30%, ${hoverCategoryData.color}50 0%, transparent 60%),
                                  radial-gradient(ellipse 50% 45% at 80% 70%, ${hoverCategoryData.color}40 0%, transparent 60%),
                                  radial-gradient(ellipse 40% 35% at 50% 100%, ${hoverCategoryData.color}30 0%, transparent 70%)
                                `,
                                backgroundSize: '200% 200%, 200% 200%, 200% 200%',
                                animation: 'peekAuroraDrift 12s ease-in-out infinite',
                                opacity: 0.85, pointerEvents: 'none', filter: 'blur(8px)',
                              },
                              '&::after': {
                                content: '""', position: 'absolute', inset: -1,
                                borderRadius: 'inherit',
                                boxShadow: `0 0 0 1px ${hoverCategoryData.color}40, 0 0 24px ${hoverCategoryData.color}25`,
                                animation: 'peekAuroraPulse 3s ease-in-out infinite',
                                pointerEvents: 'none',
                              },
                              '&:hover': {
                                transform: 'translateY(-3px) scale(1.015)',
                                boxShadow: `0 16px 40px ${hoverCategoryData.color}60, 0 0 0 1px ${hoverCategoryData.color}80`,
                              },
                              '@keyframes peekAuroraIn': {
                                '0%': { opacity: 0, transform: 'scale(0.92) translateY(12px)' },
                                '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                              },
                              '@keyframes peekAuroraDrift': {
                                '0%, 100%': { backgroundPosition: '0% 0%, 100% 100%, 50% 0%' },
                                '50%':       { backgroundPosition: '100% 100%, 0% 0%, 50% 100%' },
                              },
                              '@keyframes peekAuroraPulse': {
                                '0%, 100%': { boxShadow: `0 0 0 1px ${hoverCategoryData.color}40, 0 0 18px ${hoverCategoryData.color}20` },
                                '50%':      { boxShadow: `0 0 0 1px ${hoverCategoryData.color}60, 0 0 32px ${hoverCategoryData.color}40` },
                              },
                            }}>
                            {[
                              { top: '15%', left: '78%', dur: '5s', delay: '0s' },
                              { top: '70%', left: '12%', dur: '6.5s', delay: '0.8s' },
                              { top: '40%', left: '88%', dur: '7s', delay: '1.6s' },
                            ].map((p, k) => (
                              <Box key={k} sx={{
                                position: 'absolute', top: p.top, left: p.left,
                                width: 4, height: 4, borderRadius: '50%',
                                bgcolor: hoverCategoryData.color,
                                boxShadow: `0 0 8px ${hoverCategoryData.color}, 0 0 16px ${hoverCategoryData.color}80`,
                                opacity: 0.85, pointerEvents: 'none',
                                animation: `peekAuroraFloat ${p.dur} ease-in-out ${p.delay} infinite`,
                                '@keyframes peekAuroraFloat': {
                                  '0%, 100%': { transform: 'translate(0, 0) scale(1)', opacity: 0.5 },
                                  '50%':      { transform: 'translate(-8px, -10px) scale(1.4)', opacity: 1 },
                                },
                              }} />
                            ))}
                            <Stack spacing={0.75} sx={{ position: 'relative', zIndex: 1 }}>
                              <Typography variant="caption" sx={{
                                fontWeight: 700, letterSpacing: 1.5, fontSize: 10,
                                color: hoverCategoryData.color, textTransform: 'uppercase',
                              }}>✦ Premium Insight</Typography>
                              <Typography variant="h5" sx={{
                                fontWeight: 800, lineHeight: 1.1,
                                background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${hoverCategoryData.color} 100%)`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                              }}>{niceName(slug)}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Click to dive in
                              </Typography>
                            </Stack>
                            <ChevronRightIcon sx={{
                              position: 'absolute', bottom: 16, right: 16,
                              fontSize: 22, color: hoverCategoryData.color,
                              filter: `drop-shadow(0 0 6px ${hoverCategoryData.color})`,
                              transition: 'transform 220ms',
                            }} />
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Fade>
            );
          }

          return <Fade {...TransitionProps}><div /></Fade>;
        }}
      </Popper>

      {/* Tool of the Day spotlight */}
      {toolOfTheDay && (
        <Paper
          elevation={0}
          onClick={() => navigate(toolOfTheDay.path)}
          sx={{
            mb: 4, p: 2.5, borderRadius: 2, cursor: 'pointer',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, transparent 70%)`,
            border: `1px solid ${theme.palette.primary.main}40`,
            transition: 'all 200ms',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
              <AutoAwesomeIcon />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}>
                Tool of the day
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {toolOfTheDay.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Try something different today
              </Typography>
            </Box>
            <Button variant="contained" endIcon={<ChevronRightIcon />} sx={{ textTransform: 'none' }}>
              Open
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Recent Activity + Quick Actions — drag-reorderable + maximizable.
          Drag the title bar (with the grip icon) of either widget onto the
          other to swap their positions; order persists in localStorage.
          Click the maximize icon on either widget to expand it to fill
          the dashboard area. */}
      {(() => {
        // Persist new order on drop
        const onDrop = (target) => (e) => {
          e.preventDefault();
          if (!draggedWidget || draggedWidget === target) {
            setDraggedWidget(null);
            return;
          }
          setWidgetOrder(prev => {
            const next = [...prev];
            const fromIdx = next.indexOf(draggedWidget);
            const toIdx = next.indexOf(target);
            const tmp = next[fromIdx];
            next[fromIdx] = next[toIdx];
            next[toIdx] = tmp;
            try { localStorage.setItem('dashboardHome_widgetOrder', JSON.stringify(next)); } catch { /* ignore */ }
            return next;
          });
          setDraggedWidget(null);
        };

        const renderWidgetHeader = (id, label) => (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title="Drag to reorder">
                <Box
                  draggable
                  onDragStart={() => setDraggedWidget(id)}
                  onDragEnd={() => setDraggedWidget(null)}
                  sx={{
                    cursor: 'grab', display: 'flex', alignItems: 'center',
                    color: 'text.disabled',
                    '&:hover': { color: 'text.secondary' },
                    '&:active': { cursor: 'grabbing' },
                  }}>
                  <DragIndicatorIcon fontSize="small" />
                </Box>
              </Tooltip>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
            </Stack>
            <Tooltip title={maximizedWidget === id ? 'Collapse' : 'Maximize'}>
              <IconButton size="small"
                onClick={() => setMaximizedWidget(maximizedWidget === id ? null : id)}>
                {maximizedWidget === id ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        );

        const recentWidget = (
          <Paper elevation={0}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop('recent')}
            sx={{
              p: 3, height: '100%', borderRadius: 2,
              border: `1px solid ${draggedWidget && draggedWidget !== 'recent' ? theme.palette.primary.main : theme.palette.divider}`,
              borderStyle: draggedWidget && draggedWidget !== 'recent' ? 'dashed' : 'solid',
              transition: 'border-color 200ms, border-style 200ms',
            }}>
            {renderWidgetHeader('recent', 'Recent Activity')}
            {recentActivities.length === 0 ? (
              <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                No recent activity - start exploring the tools!
              </Typography>
            ) : (
              <List>
                {(maximizedWidget === 'recent' ? recentActivities : recentActivities.slice(0, 6)).map((activity, index, arr) => {
                  // Each row's underline color comes from the tool's own
                  // category — PDF rows underline red, Excel rows underline
                  // green, etc. Compute per-row since activities can come
                  // from any category.
                  const rowColor = getCategoryColorForPath(activity.path);
                  return (
                  <React.Fragment key={activity.id}>
                    <ListItem
                      sx={{
                        px: 1, cursor: 'pointer', borderRadius: 1, position: 'relative', overflow: 'hidden',
                        transition: 'background-color 0.2s ease, transform 0.3s ease',
                        '&:hover': { bgcolor: theme.palette.action.hover, transform: 'translateX(4px)' },
                        '&::after': {
                          content: '""', position: 'absolute',
                          bottom: 0, left: '50%',
                          width: '0%', height: '3px',
                          background: rowColor,
                          borderRadius: '2px',
                          transform: 'translateX(-50%)',
                          transition: 'width 0.35s ease-in-out',
                          backgroundImage: wavyUnderlineSvg(rowColor),
                          backgroundSize: '100% 100%',
                          pointerEvents: 'none',
                        },
                        '&:hover::after': { width: '92%' },
                      }}
                      onClick={() => navigate(activity.path)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>{getActivityIcon(activity.type)}</ListItemIcon>
                      <ListItemText
                        primary={activity.action}
                        secondary={
                          <Typography component="span" variant="caption" color="text.secondary">
                            {formatTimeAgo(activity.time)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < arr.length - 1 && <Divider />}
                  </React.Fragment>
                  );
                })}
              </List>
            )}
          </Paper>
        );

        // Quick Actions widget — user-customizable list. In edit mode,
        // each card shows a remove (×) button (except Announcements
        // which is locked) and can be drag-reordered. A "+" tile lets
        // the user add up to QUICK_ACTIONS_MAX (8) total.
        const onQuickDragStart = (idx) => () => setDraggedQuickIdx(idx);
        const onQuickDragOver = (e) => { e.preventDefault(); };
        const onQuickDrop = (toIdx) => (e) => {
          e.preventDefault();
          if (draggedQuickIdx === null || draggedQuickIdx === toIdx) {
            setDraggedQuickIdx(null);
            return;
          }
          setQuickActionsPaths(prev => {
            const next = [...prev];
            const [moved] = next.splice(draggedQuickIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
          });
          setDraggedQuickIdx(null);
        };
        const removeQuickPath = (idx) => {
          // Announcements is the only locked item — never remove it.
          if (quickActionsPaths[idx] === ANNOUNCEMENTS_PATH) return;
          setQuickActionsPaths(prev => prev.filter((_, i) => i !== idx));
        };

        const quickWidget = (
          <Paper elevation={0}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop('quick')}
            sx={{
              p: 3, height: '100%', borderRadius: 2,
              border: `1px solid ${draggedWidget && draggedWidget !== 'quick' ? theme.palette.primary.main : theme.palette.divider}`,
              borderStyle: draggedWidget && draggedWidget !== 'quick' ? 'dashed' : 'solid',
              transition: 'border-color 200ms, border-style 200ms',
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Tooltip title="Drag to reorder widget">
                  <Box
                    draggable
                    onDragStart={() => setDraggedWidget('quick')}
                    onDragEnd={() => setDraggedWidget(null)}
                    sx={{
                      cursor: 'grab', display: 'flex', alignItems: 'center',
                      color: 'text.disabled',
                      '&:hover': { color: 'text.secondary' },
                      '&:active': { cursor: 'grabbing' },
                    }}>
                    <DragIndicatorIcon fontSize="small" />
                  </Box>
                </Tooltip>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Quick Actions</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={quickActionsEditMode ? 'Done editing' : 'Customize'}>
                  <Button size="small" variant={quickActionsEditMode ? 'contained' : 'text'}
                    onClick={() => setQuickActionsEditMode(v => !v)}
                    sx={{ textTransform: 'none', minWidth: 0, px: 1 }}>
                    {quickActionsEditMode ? 'Done' : 'Edit'}
                  </Button>
                </Tooltip>
                <Tooltip title={maximizedWidget === 'quick' ? 'Collapse' : 'Maximize'}>
                  <IconButton size="small"
                    onClick={() => setMaximizedWidget(maximizedWidget === 'quick' ? null : 'quick')}>
                    {maximizedWidget === 'quick' ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              {quickActionsPaths.map((path, i) => {
                const info = getQuickActionInfo(path);
                const Icon = info.icon;
                return (
                  <Grid item xs={maximizedWidget === 'quick' ? 3 : 6} sm={maximizedWidget === 'quick' ? 3 : 6} key={path}
                    onDragOver={onQuickDragOver}
                    onDrop={onQuickDrop(i)}
                  >
                    <Card
                      draggable={quickActionsEditMode}
                      onDragStart={quickActionsEditMode ? onQuickDragStart(i) : undefined}
                      onDragEnd={() => setDraggedQuickIdx(null)}
                      onClick={() => { if (!quickActionsEditMode) navigate(path); }}
                      sx={{
                        position: 'relative',
                        bgcolor: isDark ? 'grey.800' : 'grey.100',
                        cursor: quickActionsEditMode ? 'grab' : 'pointer',
                        transition: 'all 0.2s ease',
                        outline: draggedQuickIdx === i ? `2px dashed ${theme.palette.primary.main}` : 'none',
                        outlineOffset: -4,
                        '&:hover': !quickActionsEditMode ? {
                          bgcolor: 'primary.main', color: 'primary.contrastText', transform: 'scale(1.05)',
                        } : {},
                      }}
                    >
                      {/* Edit-mode badges: PINNED tag for Announcements
                          (no remove button), × for everything else.
                          Drag handle shows on every card so any of them
                          can be reordered, including Announcements. */}
                      {quickActionsEditMode && (
                        <>
                          {info.locked ? (
                            <Tooltip title="Announcements can't be removed (but you can reorder it)">
                              <Box sx={{
                                position: 'absolute', top: 4, right: 4, zIndex: 2,
                                bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
                                px: 0.75, py: 0.1, borderRadius: 1,
                                fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                              }}>PINNED</Box>
                            </Tooltip>
                          ) : (
                            <IconButton size="small"
                              onClick={(e) => { e.stopPropagation(); removeQuickPath(i); }}
                              sx={{
                                position: 'absolute', top: 2, right: 2, zIndex: 2,
                                bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
                                width: 20, height: 20,
                                '&:hover': { bgcolor: 'error.main' },
                              }}>
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                          <Box sx={{
                            position: 'absolute', top: 4, left: 4, zIndex: 2,
                            color: 'text.disabled',
                          }}>
                            <DragIndicatorIcon sx={{ fontSize: 16 }} />
                          </Box>
                        </>
                      )}
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Icon size={32} style={{ marginBottom: 8 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {info.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {/* "+ Add" tile when edit mode is on and we have room */}
              {quickActionsEditMode && quickActionsPaths.length < QUICK_ACTIONS_MAX && (
                <Grid item xs={maximizedWidget === 'quick' ? 3 : 6} sm={maximizedWidget === 'quick' ? 3 : 6}>
                  <Card
                    onClick={() => setQuickAddOpen(true)}
                    sx={{
                      bgcolor: 'transparent', cursor: 'pointer',
                      border: `2px dashed ${theme.palette.divider}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: 'primary.main', bgcolor: `${theme.palette.primary.main}08` },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ fontSize: 32, lineHeight: 1, mb: 1, color: 'primary.main', fontWeight: 300 }}>+</Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Add tool
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: 10 }}>
                        {quickActionsPaths.length} / {QUICK_ACTIONS_MAX}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
            {quickActionsEditMode && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
                Drag any tile to reorder · click × to remove · Announcements can't be removed
              </Typography>
            )}
          </Paper>
        );

        const widgets = { recent: recentWidget, quick: quickWidget };

        // Maximized — single widget fills full width
        if (maximizedWidget) {
          return (
            <Grid container spacing={3}>
              <Grid item xs={12}>{widgets[maximizedWidget]}</Grid>
            </Grid>
          );
        }

        // Normal — render in user's chosen order
        return (
          <Grid container spacing={3}>
            {widgetOrder.map((id) => (
              <Grid item xs={12} md={6} key={id}>{widgets[id]}</Grid>
            ))}
          </Grid>
        );
      })()}

      {/* Need help / feedback footer */}
      <Paper
        elevation={0}
        sx={{
          mt: 4, p: 2, borderRadius: 2,
          bgcolor: `${theme.palette.primary.main}08`,
          border: `1px solid ${theme.palette.primary.main}30`,
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}
      >
        <HelpOutlineIcon sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
          Got feedback or need help? Ask the AI chat — it knows about every tool on Theezdocs.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setChatOpen && setChatOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Open chat
        </Button>
      </Paper>

      {/* Quick Actions — "add tool" picker dialog. Lists every interface
          the user has access to, grouped by category, filtered to those
          not already pinned. Click any to add it. */}
      <Dialog open={quickAddOpen} onClose={() => setQuickAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>Add to Quick Actions</span>
          <IconButton size="small" onClick={() => setQuickAddOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const pinnedSet = new Set(quickActionsPaths);
            const groups = allToolCategories
              .map(cat => {
                const slugs = (categoryInterfaces[cat.path] || [])
                  .filter(slug => isAdmin || isOwner || hasAccessToPath(slug))
                  .map(slug => `/dashboard/${slug}`)
                  .filter(p => !pinnedSet.has(p));
                return { cat, slugs };
              })
              .filter(g => g.slugs.length > 0);
            // Admin pseudo-category — only when the current user has the
            // admin or owner role. Filtered to admin routes not already pinned.
            if (isAdmin || isOwner) {
              const adminPaths = ADMIN_QUICK_ACTIONS
                .map(a => a.path)
                .filter(p => !pinnedSet.has(p));
              if (adminPaths.length > 0) {
                groups.push({
                  cat: { title: 'Admin', color: '#7C3AED', path: 'admin' },
                  slugs: adminPaths,
                });
              }
            }
            if (groups.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  All accessible tools are already pinned, or no tools available.
                </Typography>
              );
            }
            return groups.map(({ cat, slugs }) => (
              <Box key={cat.path} sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{
                  fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
                  color: cat.color, fontSize: 10, display: 'block', mb: 0.75,
                }}>
                  {cat.title}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {slugs.map(p => {
                    const info = getQuickActionInfo(p);
                    const Icon = info.icon;
                    return (
                      <Box key={p}
                        onClick={() => {
                          if (quickActionsPaths.length >= QUICK_ACTIONS_MAX) return;
                          setQuickActionsPaths(prev => [...prev, p]);
                          setQuickAddOpen(false);
                        }}
                        sx={{
                          p: 1.25, borderRadius: 1.5, cursor: 'pointer',
                          border: `1px solid ${theme.palette.divider}`,
                          display: 'flex', alignItems: 'center', gap: 1,
                          transition: 'all 200ms',
                          '&:hover': {
                            borderColor: cat.color, bgcolor: `${cat.color}10`, transform: 'translateY(-1px)',
                          },
                        }}>
                        <Icon size={18} color={cat.color} />
                        <Typography variant="body2" sx={{
                          fontWeight: 500, lineHeight: 1.2,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          flex: 1, minWidth: 0,
                        }}>
                          {info.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ));
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DashboardHome;
